/* global debounce, trapFocus */

if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.cartOperationQueue = Promise.resolve();
      this.init();
    }

    init() {
      this.fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      this.cartDrawer = document.getElementById('cart-drawer');
      this.itemStatus = document.getElementById('cart-line-item-status');
      this.syncQuantityState();

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('change', debounce(this.handleChange.bind(this)));
    }

    /**
     * Captures quantities without assuming that every app-controlled line is editable.
     */
    syncQuantityState() {
      const cartItems = Array.from(this.querySelectorAll('.cart-item'));
      this.currentTotalItemCount = cartItems.reduce(
        (total, item) => total + Number(item.dataset.quantity || 0),
        0
      );

      this.currentQuantities = {};
      cartItems.forEach((item) => {
        const quantityInput = item.querySelector('.qty-input__input');
        this.currentQuantities[item.dataset.key] = Number(
          quantityInput ? quantityInput.value : item.dataset.quantity
        );
      });
    }

    /**
     * Uses a purchasable cart line, never the app-controlled Gift, for recommendations.
     * @returns {string|null}
     */
    getRecommendationProductId() {
      const cartItem = Array.from(
        this.querySelectorAll('.cart-item:not([data-bayan-gift-only="true"])')
      ).find((item) => Number(item.dataset.finalLinePrice) > 0);
      return cartItem ? cartItem.dataset.productId : null;
    }

    /**
     * Handles 'click' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const removeItem = evt.target.closest('.js-remove-item');
      if (!removeItem) return;
      evt.preventDefault();
      const lineItem = removeItem.closest('.cart-item');
      if (lineItem?.dataset.key) this.updateQuantity(lineItem.dataset.key, 0);
    }

    /**
     * Handles 'change' events on the cart items element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      const lineItem = evt.target.closest('.cart-item');
      if (lineItem?.dataset.key) {
        this.updateQuantity(lineItem.dataset.key, evt.target.value, document.activeElement?.name);
      }
    }

    /**
     * Serializes native quantity mutations and app-triggered section refreshes.
     * @param {Function} operation - Cart operation to run.
     * @returns {Promise}
     */
    enqueueCartOperation(operation) {
      const nextOperation = this.cartOperationQueue.then(operation, operation);
      this.cartOperationQueue = nextOperation.catch(() => {});
      return nextOperation;
    }

    /**
     * Updates the quantity of a line item.
     * @param {string} itemKey - Current line-specific Shopify key.
     * @param {number} quantity - Quantity to set.
     * @param {string} name - Active element name.
     */
    updateQuantity(itemKey, quantity, name) {
      return this.enqueueCartOperation(() => this.performQuantityUpdate(itemKey, quantity, name));
    }

    async performQuantityUpdate(itemKey, quantity, name) {
      const lineItem = Array.from(this.querySelectorAll('.cart-item'))
        .find((item) => item.dataset.key === itemKey);
      if (!lineItem) {
        await this.performRefresh();
        const errors = document.getElementById('cart-errors');
        if (errors) {
          errors.textContent = theme.strings.cartError;
          errors.hidden = false;
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: theme.strings.cartError
          }
        }));
        return;
      }

      const line = lineItem.dataset.index;
      const cartDrawerContent = this.cartDrawer ? this.cartDrawer.querySelector('.drawer__content') : null;
      const cartDrawerContentScroll = cartDrawerContent ? cartDrawerContent.scrollTop : 0;
      const cartDrawerScroll = this.cartDrawer ? this.cartDrawer.scrollTop : 0;

      this.enableLoading(line);

      // clear all errors except this line's (which will be refreshed after this update)
      const lineErrorsId = `line-item-error-${line}`;
      const lineErrors = document.getElementById(lineErrorsId);
      document.querySelectorAll(`.cart-errors, .cart-item__error:not([id="${lineErrorsId}"])`).forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });

      const sections = this.getSectionsToRender().map((section) => section.section);
      this.fetchRequestOpts.body = JSON.stringify({
        id: itemKey,
        quantity,
        sections: [...new Set(sections)],
        sections_url: window.location.pathname
      });

      try {
        const variantId = Number(lineItem.dataset.variantId);
        const oldTotalQuantity = this.currentTotalItemCount;
        const oldQuantity = this.currentQuantities[itemKey];
        const response = await fetch(theme.routes.cartChange, this.fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        const newTotalQuantity = data.item_count;

        this.updateEmptyState(newTotalQuantity);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const { selector } = section;
          const el = sectionEl.querySelector(selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data.sections[section.section], selector);
        });

        this.itemStatus = document.getElementById('cart-line-item-status');
        this.syncQuantityState();
        this.updateRecommendations(this.getRecommendationProductId());
        this.updateLiveRegions();
        this.setFocus(line, newTotalQuantity, name);
        this.dataset.empty = newTotalQuantity === 0;
        this.currentTotalItemCount = newTotalQuantity;

        // Fire the on:line-item:change event if the line item quantity has changed
        if (oldTotalQuantity !== newTotalQuantity) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId,
              oldQuantity,
              newQuantity: Number(quantity)
            }
          }));
        }

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        this.querySelectorAll('.cart-item__loader').forEach((loader) => {
          loader.hidden = true;
        });

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

        const input = document.getElementById(`quantity-${line}`);
        input.value = input.dataset.initialValue;
        input.closest('quantity-input').currentQty = input.dataset.initialValue;
      } finally {
        this.classList.remove('pointer-events-none');

        // Attempt to maintain the same scroll position in the cart drawer
        if (cartDrawerContent) {
          requestAnimationFrame(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; });
          setTimeout(() => { cartDrawerContent.scrollTop = cartDrawerContentScroll; }, 0);
          requestAnimationFrame(() => { this.cartDrawer.scrollTop = cartDrawerScroll; });
          setTimeout(() => { this.cartDrawer.scrollTop = cartDrawerScroll; }, 0);
        }
      }
    }

    /**
     * Updates outer Drawer or Cart-page empty state after any canonical refresh.
     * @param {number} itemCount - Total item quantity after the operation.
     */
    updateEmptyState(itemCount) {
      const isEmpty = itemCount === 0;
      this.dataset.empty = isEmpty;

      if (this.cartDrawer) {
        const cartDrawerContent = this.cartDrawer.querySelector('.drawer__content');
        if (!cartDrawerContent) return;
        const cartDrawerItemsContent = this.cartDrawer.querySelector('.cart-drawer__content');
        const viewCartLink = this.cartDrawer.querySelector('.cart-drawer__view-cart');

        cartDrawerContent.classList.toggle('drawer__content--flex', isEmpty);
        if (viewCartLink) viewCartLink.hidden = isEmpty;
        if (cartDrawerItemsContent) {
          cartDrawerItemsContent.classList.toggle('grow', isEmpty);
          cartDrawerItemsContent.classList.toggle('flex', isEmpty);
          cartDrawerItemsContent.classList.toggle('items-center', isEmpty);
        }

        if (this.cartDrawer.querySelector('promoted-products')) {
          cartDrawerContent.classList.toggle('drawer__empty-with-promotions', isEmpty);
        }
      } else {
        const mainCart = this.closest('.cc-main-cart');
        const cartTitle = mainCart?.querySelector('.js-cart-title');
        const cartPageRecommendations = mainCart?.querySelector('.cart-page-recommendations');
        if (cartPageRecommendations) cartPageRecommendations.hidden = isEmpty;

        if (!isEmpty) return;

        if (cartTitle) cartTitle.style.textAlign = 'center';

        const cartSummaryCss = document.getElementById('cart-summary-css');
        if (cartSummaryCss) cartSummaryCss.remove();

        const cartContent = document.getElementById('cart-summary-and-blocks');
        if (cartContent) cartContent.hidden = true;
      }
    }

    /**
     * Refreshes the cart by rerendering its sections and updating its product recommendations.
     */
    refresh() {
      return this.enqueueCartOperation(() => this.performRefresh());
    }

    async performRefresh() {
      const errors = document.getElementById('cart-errors');
      const wasEmpty = this.dataset.empty === 'true';
      try {
        const sections = this.getSectionsToRender().map((section) => section.section);
        const response = await fetch(`?sections=${[...new Set(sections)]}`);

        // The status is 400, refresh the whole cart and stop
        if (response.status === 400 && this.cartDrawer) {
          const refreshed = await this.cartDrawer.refresh(true);
          return refreshed;
        }

        const data = await response.json();

        if (!response.ok) throw new Error(response.status);

        this.getSectionsToRender().forEach((section) => {
          const sectionEl = document.getElementById(section.id);
          if (!sectionEl) return;

          const el = sectionEl.querySelector(section.selector) || sectionEl;
          el.innerHTML = CartItems.getElementHTML(data[section.section], section.selector);
        });

        this.syncQuantityState();
        if (!this.cartDrawer && wasEmpty && this.currentTotalItemCount > 0) {
          window.location.reload();
          return true;
        }
        this.itemStatus = document.getElementById('cart-line-item-status');
        this.updateEmptyState(this.currentTotalItemCount);
        this.updateRecommendations(this.getRecommendationProductId());

        if (errors) {
          errors.innerHTML = '';
          errors.hidden = true;
        }
        return true;
      } catch (error) {
        if (errors) {
          errors.textContent = theme.strings.cartError;
          errors.hidden = false;
        }
        console.log(error); // eslint-disable-line

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));
        return false;
      }
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      let sections = [
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        },
        {
          id: 'free-shipping-notice',
          section: 'free-shipping-notice',
          selector: '.free-shipping-notice'
        }
      ];

      if (this.cartDrawer) {
        const cartDrawerId = this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '');
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: cartDrawerId,
            selector: 'cart-items'
          },
          {
            id: 'cart-promoted-products',
            section: cartDrawerId,
            selector: '#cart-promoted-products'
          },
          {
            id: 'cart-drawer',
            section: cartDrawerId,
            selector: '.cart-drawer__summary'
          },
          {
            id: 'cart-drawer-media-promotion',
            section: cartDrawerId,
            selector: '#cart-drawer-media-promotion'
          }
        ];
      } else {
        sections = [
          ...sections,
          {
            id: 'cart-items',
            section: this.dataset.section,
            selector: 'cart-items'
          }
        ];

        const cartSummary = document.getElementById('cart-summary');
        if (cartSummary?.dataset.section) {
          sections.push({
            id: 'cart-summary',
            section: cartSummary.dataset.section,
            selector: '.cart__summary'
          });
        }
      }

      return sections;
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows a loading icon over a line item.
     * @param {string} line - Line item index.
     */
    enableLoading(line) {
      this.classList.add('pointer-events-none');

      const loader = this.querySelector(`#cart-item-${line} .cart-item__loader`);
      if (loader) loader.hidden = false;

      document.activeElement.blur();
      if (this.itemStatus) this.itemStatus.setAttribute('aria-hidden', 'false');
    }

    /**
     * Updates the cart recommendations.
     * @param {string} productId - The product id for which to find recommendations.
     */
    updateRecommendations(productId) {
      this.recommendations = document.getElementById('cart-recommendations');
      if (!this.recommendations) return;

      if (productId) {
        this.recommendations.dataset.productId = productId;
        this.recommendations.init();
      } else {
        delete this.recommendations.dataset.productId;
        this.recommendations.init();
      }
    }

    /**
     * Updates the live regions.
     */
    updateLiveRegions() {
      this.itemStatus.setAttribute('aria-hidden', 'true');

      const cartStatus = document.getElementById('cart-live-region-text');
      cartStatus.setAttribute('aria-hidden', 'false');

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Traps focus in the relevant container or focuses the active element.
     * @param {number} line - Line item index.
     * @param {number} itemCount - Item count.
     * @param {string} name - Active element name.
     */
    setFocus(line, itemCount, name) {
      const lineItem = document.getElementById(`cart-item-${line}`);
      let activeEl;

      if (lineItem) {
        activeEl = lineItem.querySelector(`[name="${name}"]`);
      }

      if (this.cartDrawer) {
        if (lineItem && activeEl) {
          trapFocus(this.cartDrawer, activeEl);
        } else if (itemCount === 0) {
          const emptyTarget = this.cartDrawer.querySelector('.js-cart-empty a')
            || this.cartDrawer.querySelector('.js-close-drawer');
          trapFocus(this.cartDrawer, emptyTarget);
        } else if (this.cartDrawer.querySelector('.cart-item')) {
          trapFocus(this.cartDrawer, document.querySelector('.js-item-name'));
        }
      } else if (lineItem && activeEl) {
        activeEl.focus();
      }
    }
  }

  customElements.define('cart-items', CartItems);
}

// Monk dispatches this documented event after it changes the cart. Keep one
// bounded listener even though the cart-items asset can occur in both the Cart
// page and Drawer markup.
if (!window.bayanMonkCartRefreshBound) {
  window.bayanMonkCartRefreshBound = true;

  let monkRefreshTimer;
  let monkRefreshInFlight = false;
  let monkRefreshQueued = false;

  const refreshCartAfterMonkMutation = async () => {
    if (monkRefreshInFlight) {
      monkRefreshQueued = true;
      return;
    }

    monkRefreshInFlight = true;

    try {
      const cartItems = window.location.pathname === theme.routes.cart
        ? document.querySelector('.cc-main-cart cart-items')
        : document.querySelector('cart-drawer cart-items');

      if (cartItems && typeof cartItems.refresh === 'function') {
        await cartItems.refresh();
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      }
    } finally {
      monkRefreshInFlight = false;

      if (monkRefreshQueued) {
        monkRefreshQueued = false;
        refreshCartAfterMonkMutation();
      }
    }
  };

  document.addEventListener('cart:refresh', () => {
    clearTimeout(monkRefreshTimer);
    monkRefreshTimer = setTimeout(refreshCartAfterMonkMutation, 150);
  });
}
